import { Spinner, theme } from "@spartacommodities/sparta-react-core-components";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import type { LoaderFunctionArgs } from "react-router-dom";
import { useFetcher, useRouteError, useRouteLoaderData, useSearchParams } from "react-router-dom";
import { setDatadogUserInfo } from "src/utils/datadogRumInit.ts";
import Input, { InputStatus } from "../../components/Input/Input.tsx";
import PhoneInput from "../../components/PhoneInput/PhoneInput.tsx";
import Select from "../../components/Select/Select.tsx";
import { REGIONS } from "../../data";
import { useDatadog } from "../../hooks/useDatadog.ts";
import { CHECKBOX1_LINK, CHECKBOX1_MESSAGE, CHECKBOX_LOWER_DESCRIPTION, PRIVACY_POLICY_LINK } from "../../literals.ts";
import Checkbox from "../../components/Checkbox/Checkbox.tsx";
import { getIPInfo } from "../../services/ipAPI.ts";
import type { FormState } from "../../types";
import type { defaultActionForm } from "../utils/defaultActionForm.ts";
import { defaultErrorBoundary } from "../utils/defaultErrorBoundary.tsx";
import { trackCheckboxChange, trackRegionChange } from "../utils/inputTrack.ts";
import { getDefaultDataFromQueryParams } from "../utils/getDefaultDataFromQueryParams.ts";
import { trackFieldClicked, trackFieldFilled, trackOpenPrivacyPolicy } from "src/segment";

import { button, link, loadingButton, primaryButton, secondaryLink } from "src/styles.css.ts";
import * as styles from "../formStyles.css.ts";

export function MarketOutlookErrorBoundary() {
  const [searchParams] = useSearchParams();
  const { userData } = useRouteError() as { userData: Record<string, string> };

  return defaultErrorBoundary({ userData }, searchParams);
}

export async function marketOutlookLoader({ request }: LoaderFunctionArgs) {
  const { country_calling_code } = await getIPInfo();

  const initialData = getDefaultDataFromQueryParams(request);

  return { country_calling_code, initialData };
}

export default function MarketOutlookForm() {
  /**
   * Default trial type for the Market Outlook form.
   *
   * This constant is used to specify the default trial type for the Market Outlook signup process.
   */
  const DEFAULT_TRIAL_TYPE = "mo";

  const formRef = useRef<HTMLFormElement>(null);

  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    if (formRef.current === null) return;

    // check validity on initial load,
    // for e.g. parameters
    setIsValid(formRef.current.checkValidity());
  }, []);

  useDatadog();

  const { country_calling_code, initialData } = useRouteLoaderData("marketOutlook-signup") as Awaited<
    ReturnType<typeof marketOutlookLoader>
  >;

  const fetcher = useFetcher();
  const { errors, formState } =
    (fetcher.data as Awaited<ReturnType<typeof defaultActionForm<FormState>>> | undefined) || {};

  const handleOnFormChange = (currentTarget: HTMLFormElement) => {
    setIsValid(currentTarget.checkValidity());
  };

  return (
    <div className={styles.signupContainer} data-testid="signup-page">
      <section className={styles.sectionContainer}>
        <header className={styles.logo}>
          <img
            alt="Sparta"
            src="/svgs/marketOutlook-logo.svg"
            onError={(event) => {
              event.currentTarget.src = "/svgs/onboarding-header.svg";
            }}
          />
        </header>

        <article className={styles.article}>
          <div className={styles.message}>
            <p className={styles.title}>Experience Sparta Market Outlook free for 21 days!</p>
            <p className={styles.description} data-testid="description">
              Sign up today and enjoy a 21-day free trial with full access to all the features that have made us a
              trusted choice for traders worldwide. No risk and instant access. Start your free trial today!
            </p>
          </div>

          <fetcher.Form
            id="market-outlook-form"
            method="POST"
            noValidate
            ref={formRef}
            className={styles.formContainer}
            onChange={(e) => handleOnFormChange(e.currentTarget)}
          >
            <div className={styles.formContent}>
              <div className={styles.inputContent}>
                <div className={styles.formRow}>
                  <input name="trialType" defaultValue={DEFAULT_TRIAL_TYPE} hidden />
                  <input name="primaryProduct" defaultValue={initialData.primaryProduct ?? 14} hidden />
                  <input name="primaryProductName" defaultValue={initialData.primaryProductName ?? "Crude"} hidden />
                  <input name="userRole" defaultValue={initialData.userRole ?? 12} hidden />

                  <Input
                    placeholder="First Name"
                    name="firstName"
                    id="firstName"
                    required
                    defaultValue={initialData?.firstName ?? formState?.firstName ?? undefined}
                    onBlur={() => trackFieldFilled({ inputName: "First Name", inputValue: formState?.firstName ?? "" })}
                    onClick={() => trackFieldClicked("First Name")}
                    status={errors?.firstName ? InputStatus.ERROR : InputStatus.DEFAULT}
                    helper={{ id: "firstName-message", text: errors?.firstName ?? " " }}
                  />

                  <Input
                    placeholder="Last Name"
                    name="lastName"
                    id="lastName"
                    required
                    defaultValue={initialData.lastName ?? formState?.lastName ?? undefined}
                    onBlur={() => trackFieldFilled({ inputName: "Last Name", inputValue: formState?.lastName ?? "" })}
                    onClick={() => trackFieldClicked("Last Name")}
                    status={errors?.lastName ? InputStatus.ERROR : InputStatus.DEFAULT}
                    helper={{ id: "lastName-message", text: errors?.lastName ?? " " }}
                  />
                </div>

                <div className={styles.formRow}>
                  <Input
                    autoComplete="email"
                    placeholder="Email"
                    name="email"
                    id="email"
                    helper={{ id: "email-message", text: errors?.email ?? " " }}
                    type="email"
                    required
                    onBlur={(event) => {
                      setDatadogUserInfo(event.currentTarget.value);
                      trackFieldFilled({ inputName: "Email", inputValue: formState?.email ?? "" });
                    }}
                    onClick={() => trackFieldClicked("Email")}
                    defaultValue={initialData.email ?? formState?.email ?? undefined}
                    status={(errors?.email ?? errors?.email) ? InputStatus.ERROR : InputStatus.DEFAULT}
                  />

                  <PhoneInput
                    placeholder="Phone"
                    name="phoneNumber"
                    id="phoneNumber"
                    required
                    onBlur={() => trackFieldFilled({ inputName: "Mobile", inputValue: formState?.phoneNumber ?? "" })}
                    onClick={() => trackFieldClicked("Mobile")}
                    onClickCountrySelector={() => trackFieldClicked("Country")}
                    onSelectCountry={(country) => trackFieldFilled({ inputName: "Country", inputValue: country.name })}
                    value={initialData.phoneNumber ?? formState?.phoneNumber ?? country_calling_code}
                    status={errors?.phoneNumber ? InputStatus.ERROR : undefined}
                    helper={{ id: "phoneNumber-message", text: errors?.phoneNumber ?? " " }}
                  />
                </div>

                <div className={styles.formRow}>
                  <Select
                    type="text"
                    placeholder="Region"
                    name="region"
                    id="region"
                    required
                    value={initialData.region ?? formState?.region ?? undefined}
                    status={errors?.region ? InputStatus.ERROR : InputStatus.DEFAULT}
                    helper={{ id: "region-message", text: errors?.region ?? " " }}
                    options={REGIONS}
                    onChange={(newRegion) => {
                      if (formRef.current) handleOnFormChange(formRef.current);
                      trackRegionChange({ value: newRegion, name: newRegion });
                    }}
                    onClick={() => trackFieldClicked("Region")}
                  />

                  <Input
                    type="text"
                    placeholder="Company Name"
                    name="companyName"
                    id="companyName"
                    required
                    onBlur={() =>
                      trackFieldFilled({
                        inputName: "Company Name",
                        inputValue: formState?.companyName ?? "",
                      })
                    }
                    onClick={() => trackFieldClicked("Company Name")}
                    defaultValue={initialData?.companyName ?? formState?.companyName ?? undefined}
                    status={errors?.companyName ? InputStatus.ERROR : InputStatus.DEFAULT}
                    helper={{ id: "companyName-message", text: errors?.companyName ?? " " }}
                  />
                </div>
              </div>
              <div className={styles.checkboxesContent}>
                <Checkbox
                  name="subscribeNewsletter"
                  id="subscribeNewsletter"
                  defaultChecked={initialData.subscribeNewsletter ?? formState?.subscribeNewsletter}
                  onChange={trackCheckboxChange("subscribeNewsletter")}
                >
                  <p className={clsx(styles.text, styles.checkboxLabel)}>
                    {CHECKBOX1_MESSAGE}
                    <a
                      className={clsx(link, secondaryLink, styles.text, styles.checkboxLabel)}
                      href={PRIVACY_POLICY_LINK}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => trackOpenPrivacyPolicy()}
                    >
                      {CHECKBOX1_LINK}
                    </a>
                  </p>
                </Checkbox>

                <p className={clsx(styles.text, styles.underCheckboxDescription)} data-testid="disclaimer">
                  {CHECKBOX_LOWER_DESCRIPTION}
                </p>
              </div>
            </div>

            <button
              className={clsx(button, primaryButton, styles.submitButton, fetcher.state !== "idle" && loadingButton)}
              type="submit"
              name="signup"
              disabled={!isValid}
              aria-disabled={!isValid}
            >
              {fetcher.state !== "idle" ? (
                <Spinner width={23} height={23} backgroundColor={theme.colors.neutral.dark_00} />
              ) : (
                "Sign up"
              )}
            </button>
          </fetcher.Form>
        </article>
      </section>
    </div>
  );
}
